import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export function PrivacyPolicyES() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-indigo-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center text-indigo-600 hover:text-indigo-700 mb-6 transition-colors duration-200"
        >
          <ArrowLeft className="mr-2 h-5 w-5" />
          Volver a la aplicación
        </button>

        <article className="bg-white rounded-lg shadow-xl p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Política de Privacidad</h1>

          <div className="space-y-6 text-gray-700">
            <p className="text-sm text-gray-500">
              Última actualización: {new Date().toLocaleDateString('es-ES')}
            </p>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Responsable del Tratamiento de Datos</h2>
              <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                <p className="leading-relaxed">
                  <strong>El responsable del tratamiento es:</strong>
                </p>
                <div className="ml-4 space-y-1">
                  <p><strong>Nombre:</strong> Puretech s.r.o.</p>
                  <p><strong>Número de Registro:</strong> 05503795</p>
                  <p><strong>Ubicación:</strong> República Checa</p>
                  <p><strong>Contacto:</strong> support@simplestickers.app</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Información que Recopilamos</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  <strong>Información de la Cuenta:</strong> Cuando crea una cuenta, recopilamos su dirección de correo electrónico y cualquier información de perfil que decida proporcionar.
                </p>
                <p className="leading-relaxed">
                  <strong>Datos de Uso:</strong> Recopilamos información sobre cómo utiliza nuestro servicio, incluidas las tareas creadas, los proyectos administrados y los patrones de uso de funciones.
                </p>
                <p className="leading-relaxed">
                  <strong>Información de Pago:</strong> El procesamiento de pagos es manejado por Polar. No almacenamos los detalles de su tarjeta de pago en nuestros servidores.
                </p>
                <p className="leading-relaxed">
                  <strong>Datos Técnicos:</strong> Recopilamos automáticamente cierta información técnica, incluida la dirección IP, el tipo de navegador, el sistema operativo y la información del dispositivo.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. Cómo Utilizamos Su Información</h2>
              <div className="space-y-2">
                <p className="leading-relaxed">Utilizamos la información que recopilamos para:</p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Proporcionar, mantener y mejorar nuestros servicios</li>
                  <li>Procesar transacciones y gestionar suscripciones</li>
                  <li>Enviarle avisos técnicos y mensajes de soporte</li>
                  <li>Responder a sus comentarios y preguntas</li>
                  <li>Analizar patrones de uso para mejorar la experiencia del usuario</li>
                  <li>Hacer cumplir nuestros términos y condiciones</li>
                  <li>Cumplir con las obligaciones legales</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Compartir Información</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  No vendemos, intercambiamos ni transferimos su información personal a terceros, excepto como se describe en esta política:
                </p>
                <ul className="list-disc ml-6 space-y-1">
                  <li><strong>Proveedores de Servicios:</strong> Podemos compartir información con terceros de confianza que nos ayudan a operar nuestro servicio (por ejemplo, Polar para el procesamiento de pagos, Supabase para servicios de base de datos)</li>
                  <li><strong>Requisitos Legales:</strong> Podemos divulgar información cuando lo exija la ley o para proteger nuestros derechos</li>
                  <li><strong>Transferencias Comerciales:</strong> En caso de una fusión, adquisición o venta de activos, la información del usuario puede ser transferida</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Seguridad de los Datos</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  Implementamos medidas de seguridad técnicas y organizativas apropiadas para proteger su información personal contra el acceso no autorizado, alteración, divulgación o destrucción. Sin embargo, ningún método de transmisión por Internet es 100% seguro.
                </p>
                <p className="leading-relaxed">
                  <strong>Respuesta a Violaciones de Seguridad:</strong> En caso de una violación de datos personales, cumpliremos con el RGPD:
                </p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Informar del incidente a la autoridad de supervisión dentro de las 72 horas posteriores al descubrimiento</li>
                  <li>Notificar a los usuarios afectados si la violación representa un alto riesgo para sus derechos y libertades</li>
                  <li>Tomar medidas para minimizar el impacto y prevenir futuros incidentes</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Retención de Datos</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  Conservamos su información personal durante el tiempo necesario para proporcionar nuestros servicios. Los períodos de retención específicos son:
                </p>
                <ul className="list-disc ml-6 space-y-1">
                  <li><strong>Datos de la cuenta:</strong> Duración del servicio + 30 días después de la eliminación de la cuenta</li>
                  <li><strong>Contenido de tareas (notas adhesivas):</strong> Hasta que las elimine o cancele la cuenta</li>
                  <li><strong>Registros de pago:</strong> 7 años (requisito de la ley de contabilidad checa)</li>
                  <li><strong>Datos analíticos:</strong> Hasta 26 meses</li>
                  <li><strong>Datos de copia de seguridad:</strong> Pueden persistir hasta 90 días después de la eliminación principal</li>
                  <li><strong>Datos anonimizados:</strong> Conservados indefinidamente con fines estadísticos</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Sus Derechos según el RGPD</h2>
              <div className="space-y-2">
                <p className="leading-relaxed">Usted tiene derecho a:</p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Acceder a la información personal que tenemos sobre usted</li>
                  <li>Corregir cualquier información personal inexacta</li>
                  <li>Solicitar la eliminación de su información personal</li>
                  <li>Oponerse o restringir el procesamiento de su información</li>
                  <li>Portabilidad de datos (recibir una copia de sus datos en un formato estructurado)</li>
                  <li>Retirar el consentimiento en cualquier momento</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Cookies y Seguimiento</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  Utilizamos cookies y tecnologías de seguimiento similares para mejorar su experiencia. Tipos de cookies que utilizamos:
                </p>
                <ul className="list-disc ml-6 space-y-1">
                  <li><strong>Cookies esenciales:</strong> Necesarias para la funcionalidad del servicio (autenticación, inicio de sesión)</li>
                  <li><strong>Cookies analíticas:</strong> Nos ayudan a comprender cómo utiliza nuestro servicio (puede rechazarlas)</li>
                  <li><strong>Cookies de terceros:</strong> Google OAuth, Polar para pagos</li>
                </ul>
                <p className="leading-relaxed">
                  Puede controlar la configuración de cookies a través de las preferencias de su navegador o nuestro banner de cookies en la primera visita.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Servicios de Terceros</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">Nuestro servicio se integra con servicios de terceros:</p>
                <ul className="list-disc ml-6 space-y-1">
                  <li><strong>Google OAuth:</strong> Para autenticación (sujeto a la política de privacidad de Google)</li>
                  <li><strong>Polar:</strong> Para el procesamiento de pagos (sujeto a la política de privacidad de Polar)</li>
                  <li><strong>Supabase:</strong> Para servicios de base de datos y autenticación</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Privacidad de los Niños</h2>
              <p className="leading-relaxed">
                Nuestro servicio no está dirigido a niños menores de 16 años. No recopilamos a sabiendas información personal de niños menores de 16 años. Si es padre o tutor y cree que su hijo nos ha proporcionado información personal, contáctenos en support@simplestickers.app.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. Transferencias Internacionales de Datos</h2>
              <p className="leading-relaxed">
                Su información puede ser transferida y procesada en países distintos a su país de residencia. Nos aseguramos de que existan salvaguardas apropiadas para proteger su información personal de acuerdo con las leyes de protección de datos aplicables.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">12. Cambios en Esta Política</h2>
              <p className="leading-relaxed">
                Podemos actualizar esta política de privacidad de vez en cuando. Le notificaremos cualquier cambio material publicando la nueva política en esta página y actualizando la fecha de "Última actualización".
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">13. Toma de Decisiones Automatizada</h2>
              <p className="leading-relaxed">
                Nuestro servicio utiliza algoritmos de IA para generar sugerencias de tareas. Esta función es puramente asistencial y no crea decisiones con efectos legales o significativos en los usuarios. Puede optar por no utilizar sugerencias en cualquier momento.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">14. Autoridad de Supervisión</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  Tiene derecho a presentar una queja ante la autoridad de supervisión de protección de datos:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p><strong>Oficina Checa de Protección de Datos Personales (ÚOOÚ)</strong></p>
                  <p className="text-sm mt-2">Pplk. Sochora 27, 170 00 Praga 7</p>
                  <p className="text-sm">Web: <a href="https://www.uoou.cz" className="text-indigo-600 hover:underline">www.uoou.cz</a></p>
                  <p className="text-sm">Correo electrónico: posta@uoou.cz</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">15. Cómo Ejercer Sus Derechos</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  Para ejercer sus derechos según el RGPD (acceso a datos, rectificación, eliminación, portabilidad, objeción, etc.):
                </p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Envíenos una solicitud a <strong>support@simplestickers.app</strong></li>
                  <li>Responderemos dentro de <strong>1 mes</strong> después de recibir su solicitud</li>
                  <li>Podemos solicitarle que verifique su identidad</li>
                  <li>El servicio es <strong>gratuito</strong> (a menos que la solicitud sea manifiestamente infundada o excesiva)</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">16. Contáctenos</h2>
              <p className="leading-relaxed">
                Si tiene alguna pregunta sobre esta política de privacidad o nuestras prácticas de datos, contáctenos:
              </p>
              <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Correo electrónico:</strong> support@simplestickers.app
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Para todas las preguntas relacionadas con la privacidad, solicitudes de datos, soporte y otras consultas
                </p>
              </div>
            </section>
          </div>
        </article>
      </div>
    </div>
  );
}
